import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

// Storage failures (private browsing / quota) must never block site entry.
const dismissedInMemory = new Set();
const storageKey = (tenantId, banner) => `serviceemp:welcome:${JSON.stringify([
  tenantId, banner.id, banner.image_url, banner.updated_at || '',
])}`;
const wasDismissed = (key) => {
  if (dismissedInMemory.has(key)) return true;
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
};

export function welcomeImageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = new URL(value, base);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch { return null; }
}

export default function WelcomeBanner() {
  const location = useLocation();
  const tenantSlug = tenantSlugFromPathOrHost(location.pathname);
  // Never cover admin/staff, payment return, callbacks, or an in-progress flow.
  const parts = location.pathname.split('/').filter(Boolean);
  if (parts[0] === tenantSlug) parts.shift();
  const eligible = Boolean(tenantSlug) && (
    parts.length === 0 || (parts.length === 1 && ['auth', 'app'].includes(parts[0]))
  ) && !location.hash.includes('session_id=');
  const [active, setActive] = useState(null);

  useEffect(() => {
    setActive(null);
    if (!eligible) return undefined;
    let cancelled = false;
    let preload;
    const controller = new AbortController();
    api.get('/api/promo-banners', {
      params: { placement: 'welcome' },
      headers: { 'X-Tenant-Slug': tenantSlug },
      signal: controller.signal,
      timeout: 8000,
    }).then(({ data }) => {
      // No platform fallback, including when an invalid tenant was requested.
      if (cancelled || !data?.tenant_id) return;
      const banner = (data.banners || []).find(b => b.enabled !== false && b.placement === 'welcome' && welcomeImageUrl(b.image_url));
      if (!banner) return;
      const key = storageKey(data.tenant_id, banner);
      if (wasDismissed(key)) return;
      const imageUrl = welcomeImageUrl(banner.image_url);
      preload = new Image();
      preload.onload = () => {
        if (!cancelled) setActive({ banner, key, imageUrl, tenantSlug });
      };
      // Failed images leave the underlying site completely usable.
      preload.src = imageUrl;
    }).catch(() => {});
    return () => {
      cancelled = true;
      controller.abort();
      if (preload) preload.onload = null;
    };
  }, [eligible, tenantSlug]);

  const close = () => {
    if (active) {
      dismissedInMemory.add(active.key);
      try { sessionStorage.setItem(active.key, '1'); } catch { /* optional storage */ }
    }
    setActive(null);
  };

  if (!eligible || !active || active.tenantSlug !== tenantSlug) return null;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent
        overlayClassName="z-[10000] bg-black/70"
        className="z-[10001] w-[calc(100%-24px)] max-w-md rounded-2xl border-white/20 bg-[#14213b] px-3 pb-3 pt-14 text-white gap-0 [&>button]:top-2 [&>button]:right-2 [&>button]:w-11 [&>button]:h-11 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-white/10 [&>button]:opacity-100"
        style={{ maxHeight: 'calc(100dvh - 24px)', overflowY: 'auto' }}
        data-testid="welcome-banner-popup"
      >
        <DialogTitle className="sr-only">{active.banner.title || 'Welcome'}</DialogTitle>
        <DialogDescription className="sr-only">Welcome poster. Close to continue to the site.</DialogDescription>
        <img
          src={active.imageUrl}
          alt={active.banner.title || 'Welcome poster'}
          className="w-full h-auto object-contain rounded-xl"
          style={{ maxHeight: 'calc(100dvh - 100px)' }}
          onError={close}
          data-testid="welcome-banner-image"
        />
      </DialogContent>
    </Dialog>
  );
}
